import { useEffect, useRef, useCallback } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import * as Sentry from '@sentry/react';
import type { Message, Participant } from '../components/practice/types';
import { getApiBaseUrlForBrowserFetch, PREVIEW_PR } from '../lib/previewApiLookup';

export interface SessionStreamCallbacks {
  onMessage: (msg: Message) => void;
  onParticipantJoined: (participant: Participant) => void;
  onSessionEnded: () => void;
  onSpeakingStarted?: (userId: string, userName: string | null) => void;
  onSpeakingStopped?: (userId: string) => void;
  onError?: (error: Error) => void;
}

export function useSessionStream(
  sessionId: string | undefined,
  enabled: boolean,
  callbacks: SessionStreamCallbacks,
) {
  const abortRef = useRef<AbortController | null>(null);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    if (!sessionId || !enabled) return;

    const token = sessionStorage.getItem('token');
    if (!token) return;

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let cancelled = false;

    void (async () => {
      let base: string;
      try {
        base = await getApiBaseUrlForBrowserFetch();
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to resolve API URL for session stream');
        Sentry.captureException(error, { tags: { subsystem: 'session_stream', sessionId } });
        if (!cancelled) callbacksRef.current.onError?.(error);
        return;
      }
      if (cancelled) return;

      const streamHeaders: Record<string, string> = { Authorization: `Bearer ${token}` };
      if (PREVIEW_PR) streamHeaders['X-Preview-PR'] = PREVIEW_PR;

      fetchEventSource(`${base}/practice/sessions/${sessionId}/stream`, {
        headers: streamHeaders,
        signal: ctrl.signal,
        onmessage(ev) {
          try {
            const data = JSON.parse(ev.data);
            switch (ev.event) {
              case 'message_added':
                callbacksRef.current.onMessage(data as Message);
                break;
              case 'participant_joined':
                callbacksRef.current.onParticipantJoined(data as Participant);
                break;
              case 'session_ended':
                callbacksRef.current.onSessionEnded();
                break;
              case 'speaking_started':
                callbacksRef.current.onSpeakingStarted?.(data.userId, data.userName);
                break;
              case 'speaking_stopped':
                callbacksRef.current.onSpeakingStopped?.(data.userId);
                break;
            }
          } catch {
            // skip malformed events
          }
        },
        onerror() {
          // fetch-event-source will retry automatically
        },
      });
    })();

    return () => {
      cancelled = true;
      ctrl.abort();
      abortRef.current = null;
    };
  }, [sessionId, enabled]);

  const postSpeaking = useCallback(
    async (speaking: boolean) => {
      if (!sessionId) return;
      const token = sessionStorage.getItem('token');
      if (!token) return;
      try {
        const base = await getApiBaseUrlForBrowserFetch();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };
        if (PREVIEW_PR) headers['X-Preview-PR'] = PREVIEW_PR;
        await fetch(`${base}/practice/sessions/${sessionId}/speaking`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ speaking }),
        });
      } catch (err) {
        Sentry.captureException(err, { tags: { subsystem: 'post_speaking', sessionId } });
      }
    },
    [sessionId],
  );

  return { postSpeaking };
}
